import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Ruler, ArrowRight, TrendingUp, BarChart3, Users, Award, Building2, Heart, Activity, School, LineChart } from "lucide-react";
import useTheme from "@/utils/useTheme";
import ToolPageNavbar from "@/components/sections/ToolPageNavbar";
import Footer from "@/components/sections/Footer";

const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_83ea495c-8bdb-4889-9a46-037ad22cb3df/artifacts/wx8t37eh_For%20Growth%20page11.png",
  features: "https://customer-assets.emergentagent.com/job_83ea495c-8bdb-4889-9a46-037ad22cb3df/artifacts/f7ip8czd_features.png",
  features2: "https://customer-assets.emergentagent.com/job_83ea495c-8bdb-4889-9a46-037ad22cb3df/artifacts/74ggivc7_future-visions-business-technology-concept.jpg",
  hospital: "/who-we-help-hospital.png",
  ngo: "/who-we-help-ngo.png",
  diagnostic: "/who-we-help-diagnostic.png",
  school: "/who-we-help-school.png",
};

const features = [
  { icon: Ruler, title: "Predicted Adult Height", description: "Using the published Bayley-Pinneau method with bone age, parental midpoint heights and ethnicity-adjusted population data, predict the child's final adult height years in advance.", color: "#4ECDC4" },
  { icon: LineChart, title: "IAP-2015 Growth Curves", description: "Interactive IAP-Khadilkar 2015 percentile growth curves (3rd → 97th) — with WHO MGRS for ages below 5 — for height-for-age and weight-for-age with growth-spurt zone visualisation and present-position markers.", color: "#A855F7" },
  { icon: BarChart3, title: "Growth Health Index", description: "A proprietary composite score (0-100) combining bone-age difference, weight status and growth remaining into a single actionable metric — instantly comparable across visits.", color: "#FF6B6B" },
  { icon: TrendingUp, title: "APHV & maturity status", description: "Age at Peak Height Velocity prediction plus carpal-bone maturity stage, telling parents and pediatricians exactly where the child is on the puberty timeline.", color: "#FFE66D" },
  { icon: Award, title: "Weight-for-height assessment", description: "Predicted vs present weight with categorisation (normal / slightly above / significantly below, etc.) using gender-specific Indian height-weight reference data.", color: "#4ECDC4" },
  { icon: Users, title: "Parental-midpoint integration", description: "Optional parental heights refine the prediction using the genetic mid-parental height formula — giving a combined X-ray + Parental adult-height estimate.", color: "#A855F7" },
];

const whoWeHelp = [
  { icon: Building2, title: "Hospitals", description: "Routine growth tracking for pediatric departments", image: IMAGES.hospital },
  { icon: Heart, title: "NGOs", description: "Population-level child-nutrition and growth screening", image: IMAGES.ngo },
  { icon: Activity, title: "Diagnostic Centers", description: "Add growth assessment as a value-add service", image: IMAGES.diagnostic },
  { icon: School, title: "Schools", description: "Annual growth screening for all enrolled students", image: IMAGES.school },
];

const howItWorks = [
  { step: "01", title: "Gather inputs", description: "Note the child's height, weight, bone age (from an X-ray report or our Bone Age Prediction tool) and parental heights." },
  { step: "02", title: "Enter the data", description: "Fill the simple form on the Growth Assessment page — no X-ray upload required." },
  { step: "03", title: "Get the analysis", description: "Predicted adult height (X-ray, Parental, and Combined), APHV, GHI score, maturity assessment and weight status." },
  { step: "04", title: "Track over time", description: "Re-run quarterly and download branded PDFs to compare the child's growth trajectory visit-over-visit." },
];

const GrowthAssessmentAboutPage = () => {
  useTheme();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen dark bg-mesh">
      <ToolPageNavbar />
      <main className="pt-28 pb-16">
        {/* Hero */}
        <section className="max-w-6xl mx-auto px-6 mb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}>
              <div className="w-16 h-16 rounded-2xl mb-6 flex items-center justify-center bg-[#4ECDC4]/10 border border-[#4ECDC4]/20">
                <Ruler className="w-8 h-8 text-[#4ECDC4]" />
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-white mb-4" data-testid="growth-about-title">
                Growth <span className="text-[#4ECDC4]">Assessment</span>
              </h1>
              <p className="text-lg text-white/50 mb-4">Predicted adult height, GHI score & IAP-2015 percentile curves — no X-ray upload needed.</p>
              <p className="text-base text-white/40 leading-relaxed mb-8">
                The most comprehensive growth-tracking tool for Indian children. Plug in height, weight, bone age and parental heights —
                we deliver predicted adult height (Bayley-Pinneau + parental midpoint, ethnicity-adjusted), Age at Peak Height Velocity,
                Growth Health Index, and an interactive WHO growth-percentile chart all in one branded report.
              </p>
              <Link to="/tools/growth" className="inline-flex items-center gap-2 bg-[#4ECDC4] hover:bg-[#3dbdb5] text-[#0a1628] font-semibold px-8 py-4 rounded-xl text-lg transition-colors" data-testid="growth-try-now-btn">
                Try Growth Assessment <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              <div className="glass-card p-3 rounded-2xl overflow-hidden">
                <img src={IMAGES.hero} alt="Growth Assessment" className="w-full h-80 object-cover rounded-xl" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features */}
        <section className="max-w-6xl mx-auto px-6 mb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="glass-card p-3 rounded-2xl overflow-hidden">
                <img src={IMAGES.features2} alt="Growth trajectory" className="w-full rounded-xl object-cover aspect-square" />
              </div>
            </motion.div>
            <div>
              <h2 className="font-display text-3xl text-white mb-6">What this tool offers</h2>
              <div className="space-y-5">
                {features.map((f, i) => (
                  <motion.div key={i} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                    className="flex items-start gap-4" data-testid={`growth-feature-${i}`}>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${f.color}15` }}>
                      <f.icon className="w-5 h-5" style={{ color: f.color }} />
                    </div>
                    <div>
                      <h3 className="font-display text-base text-white mb-1">{f.title}</h3>
                      <p className="text-sm text-white/45 leading-relaxed">{f.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* How */}
        <section className="max-w-4xl mx-auto px-6 mb-20">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl text-white mb-3">How It Works</h2>
            <p className="text-white/40">Four steps from inputs to a complete growth report</p>
          </div>
          <div className="space-y-5">
            {howItWorks.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="flex items-start gap-5 glass-card p-5" data-testid={`growth-step-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-[#4ECDC4]/10 flex items-center justify-center shrink-0">
                  <span className="text-lg font-bold text-[#4ECDC4]">{s.step}</span>
                </div>
                <div>
                  <h3 className="font-display text-lg text-white mb-1">{s.title}</h3>
                  <p className="text-sm text-white/50">{s.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Why */}
        <section className="max-w-4xl mx-auto px-6 mb-20" data-testid="why-growth-section">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-6 text-center">Why growth tracking matters</h2>
          <div className="space-y-4 text-white/55 leading-relaxed text-base md:text-lg">
            <p>
              Routine height-and-weight monitoring is the single most effective early-warning system for childhood
              endocrine, nutritional and developmental disorders. A child whose growth velocity falls outside the
              expected percentile band may need a referral 12-18 months before any visible symptoms.
            </p>
            <p>
              This tool combines <strong className="text-white/80">predicted adult height</strong>, <strong className="text-white/80">Age at Peak Height Velocity (APHV)</strong>,
              the proprietary <strong className="text-white/80">Growth Health Index (GHI)</strong> and WHO-standard percentile
              charts in a single branded PDF — making it easy for parents to share the child&apos;s growth journey
              with any pediatrician at any clinic.
            </p>
            <p>
              Don&apos;t have a bone-age value yet? Use our companion <Link to="/tools/bone-age" className="text-[#4ECDC4] underline">Bone Age Prediction</Link>
              {" "}tool to get it from an X-ray in seconds, then come back here.
            </p>
          </div>
        </section>

        {/* Who We Help */}
        <section className="max-w-6xl mx-auto px-6 mb-20">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl text-white mb-3">Who We Help</h2>
            <p className="text-white/40">Trusted across pediatric care and screening programs</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whoWeHelp.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className="glass-card overflow-hidden group">
                  <img src={item.image} alt={item.title} className="w-full h-44 object-contain bg-[#0A1029] p-2 group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" />
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-4 h-4 text-[#4ECDC4]" />
                      <h3 className="font-display text-base text-white">{item.title}</h3>
                    </div>
                    <p className="text-xs text-white/45">{item.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>

        <section className="max-w-3xl mx-auto px-6 text-center">
          <Link to="/tools/growth" className="inline-flex items-center gap-2 bg-[#4ECDC4] hover:bg-[#3dbdb5] text-[#0a1628] font-semibold px-10 py-4 rounded-xl text-lg transition-colors">
            Start Growth Assessment <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default GrowthAssessmentAboutPage;
